import { useEffect, useState } from 'react';
import { apiService, DashboardSiteBilling } from '@/api/apiService';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { toast } from 'react-hot-toast';

type Props = {
  siteId: number | null;
  /** Optional parent billing; card fetches site billing when missing or account-scoped. */
  billing?: DashboardSiteBilling | null;
  onRefresh?: () => Promise<void>;
};

function formatHours(hours: number): string {
  if (hours < 1) return hours.toFixed(2);
  if (hours < 10) return hours.toFixed(2);
  return hours.toFixed(1);
}

export default function DashboardChatbotRuntimeCard({ siteId, billing: parentBilling, onRefresh }: Props) {
  const [busy, setBusy] = useState(false);
  const [billing, setBilling] = useState<DashboardSiteBilling | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!siteId) {
      setBilling(null);
      setLoadError('');
      return;
    }

    if (parentBilling && parentBilling.scope !== 'account') {
      setBilling(parentBilling);
      setLoadError('');
      return;
    }

    let cancelled = false;
    setLoading(true);
    setLoadError('');
    apiService
      .dashboardGetBilling(siteId)
      .then((data) => {
        if (!cancelled) {
          setBilling(data);
        }
      })
      .catch((error: any) => {
        if (!cancelled) {
          setBilling(null);
          setLoadError(error?.response?.data?.detail || 'Failed to load chatbot runtime status');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [siteId, parentBilling]);

  if (!siteId) {
    return null;
  }

  if (loading && !billing) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Chatbot runtime</CardTitle>
          <CardDescription>Loading chatbot status…</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (loadError && !billing) {
    return (
      <Card className="border-destructive/40">
        <CardHeader>
          <CardTitle>Chatbot runtime</CardTitle>
          <CardDescription className="text-destructive">{loadError}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (!billing) {
    return null;
  }

  const running = billing.chatbot_running !== false;
  const hourlyRate = Number(billing.hourly_runtime_rate_usd ?? 0.0137);
  const runningHours = Number(billing.running_hours_total ?? 0);
  const retentionDays = billing.chat_data_retention_days ?? 90;
  const daysUntilDeletion = billing.days_until_chat_data_deletion;

  const refreshAll = async () => {
    if (siteId) {
      const fresh = await apiService.dashboardGetBilling(siteId);
      setBilling(fresh);
    }
    if (onRefresh) {
      await onRefresh();
    }
  };

  const handleToggle = async (nextRunning: boolean) => {
    if (!siteId) return;
    const confirmStop = !nextRunning
      ? window.confirm(
          `Stop the chatbot? It will disappear from your website and hourly billing will pause. ` +
            `If you do not start it again within ${retentionDays} days, all chat data will be deleted.`
        )
      : true;
    if (!confirmStop) return;

    setBusy(true);
    try {
      await apiService.dashboardUpdateChatbotRunning(siteId, nextRunning);
      toast.success(nextRunning ? 'Chatbot started' : 'Chatbot stopped');
      await refreshAll();
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to update chatbot');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className={!running ? 'border-amber-500/50 bg-amber-500/5' : undefined}>
      <CardHeader>
        <CardTitle>Chatbot runtime</CardTitle>
        <CardDescription>
          While running, the widget is live on your site and billed at ${hourlyRate.toFixed(4)}/hr (AWS-style).
          Stop it anytime to pause hourly charges and hide the widget.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-medium">{running ? 'Running' : 'Stopped'}</p>
            <p className="text-xs text-muted-foreground">
              {running
                ? billing.hourly_runtime_billing_active
                  ? 'Hourly runtime billing is active'
                  : 'Widget visible — hourly billing exempt or balance not applicable'
                : 'Widget hidden on your website'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{running ? 'On' : 'Off'}</span>
            <Switch checked={running} disabled={busy} onCheckedChange={handleToggle} />
          </div>
        </div>

        <div className="grid gap-2 text-sm sm:grid-cols-2">
          <div className="rounded-md border px-3 py-2">
            <p className="text-xs text-muted-foreground">Running hours (billed)</p>
            <p className="text-lg font-semibold">{formatHours(runningHours)} h</p>
          </div>
          <div className="rounded-md border px-3 py-2">
            <p className="text-xs text-muted-foreground">Hourly rate</p>
            <p className="text-lg font-semibold">${hourlyRate.toFixed(4)}/hr</p>
          </div>
        </div>

        {!running && daysUntilDeletion != null ? (
          <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm">
            <p className="font-medium">
              {daysUntilDeletion === 0
                ? 'Chat data scheduled for deletion soon'
                : `${daysUntilDeletion} day${daysUntilDeletion === 1 ? '' : 's'} until chat data is deleted`}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              After {retentionDays} days stopped without restarting, crawled content, conversations, and
              bookings for this site are permanently removed. Start the chatbot again to reset the timer.
            </p>
          </div>
        ) : running ? (
          <p className="text-xs text-muted-foreground">
            If you stop the chatbot, you have {retentionDays} days to start it again before chat data is
            deleted. Warning emails are sent 30 days and 2 days before deletion.
          </p>
        ) : null}

        {!running ? (
          <Button type="button" size="sm" disabled={busy} onClick={() => handleToggle(true)}>
            Start chatbot
          </Button>
        ) : (
          <Button type="button" size="sm" variant="destructive" disabled={busy} onClick={() => handleToggle(false)}>
            Stop chatbot
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
